import { Subject, Observable } from 'rxjs';
import { MapClickEvent, MarkerClickEvent, MapMoveEvent, MapMouseMoveEvent } from '../interfaces';

declare var L: any;

/**
 * Click handler with priority
 */
export interface ClickHandler {
  id: string;
  priority: number; // Higher priority = executed first
  handler: (event: MapClickEvent) => boolean; // Return true to stop propagation
}

/**
 * Map Events Controller - handles all map event emissions
 * Manages all RxJS Subjects and Observables for map events
 * Centralized click handling with priority system
 */
export class MapEventsController {
  private mapClickSubject = new Subject<MapClickEvent>();
  private mapDblClickSubject = new Subject<MapClickEvent>();
  private mapRightClickSubject = new Subject<MapClickEvent>();
  private markerClickSubject = new Subject<MarkerClickEvent>();
  private polygonClickSubject = new Subject<MarkerClickEvent>();
  private coordinatesChangeSubject = new Subject<[number, number]>();
  private mapMoveSubject = new Subject<MapMoveEvent>();
  private mapDragEndSubject = new Subject<MapMoveEvent>();
  private mapZoomChangeSubject = new Subject<number>();
  private mapMouseMoveSubject = new Subject<MapMouseMoveEvent>();
  private shapesChangedSubject = new Subject<any[]>();
  private monitoringPointsClickSubject = new Subject<any>();

  // Vector tile feature click subject
  private vectorTileFeatureClickSubject = new Subject<{ feature: any, layerId: string, latlng: { lat: number, lng: number } }>();

  // Click handlers with priority
  private clickHandlers: ClickHandler[] = [];

  // Reference to map instance for popup
  private mapInstance: any = null;

  // Public observables for component subscriptions
  public mapClick$: Observable<MapClickEvent> = this.mapClickSubject.asObservable();
  public mapDblClick$: Observable<MapClickEvent> = this.mapDblClickSubject.asObservable();
  public mapRightClick$: Observable<MapClickEvent> = this.mapRightClickSubject.asObservable();
  public markerClick$: Observable<MarkerClickEvent> = this.markerClickSubject.asObservable();
  public polygonClick$: Observable<MarkerClickEvent> = this.polygonClickSubject.asObservable();
  public coordinatesChange$: Observable<[number, number]> = this.coordinatesChangeSubject.asObservable();
  public mapMove$: Observable<MapMoveEvent> = this.mapMoveSubject.asObservable();
  /** End of pan drag only (not emitted after zoom — reload bbox without issuing a request on every zoom). */
  public mapDragEnd$: Observable<MapMoveEvent> = this.mapDragEndSubject.asObservable();
  public mapZoomChange$: Observable<number> = this.mapZoomChangeSubject.asObservable();
  public mapMouseMove$: Observable<MapMouseMoveEvent> = this.mapMouseMoveSubject.asObservable();
  public shapesChanged$: Observable<any[]> = this.shapesChangedSubject.asObservable();
  public monitoringPointsClick$: Observable<any> = this.monitoringPointsClickSubject.asObservable();
  public vectorTileFeatureClick$: Observable<{ feature: any, layerId: string, latlng: { lat: number, lng: number } }> = this.vectorTileFeatureClickSubject.asObservable();

  /**
   * Set map instance reference
   */
  setMapInstance(map: any): void {
    this.mapInstance = map;
  }

  /**
   * Register a click handler with priority
   * Higher priority handlers are executed first
   * If handler returns true, click propagation stops
   */
  registerClickHandler(id: string, priority: number, handler: (event: MapClickEvent) => boolean): void {
    // Remove existing handler with same id
    this.unregisterClickHandler(id);

    this.clickHandlers.push({ id, priority, handler });
    // Sort by priority descending (higher priority first)
    this.clickHandlers.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Unregister a click handler
   */
  unregisterClickHandler(id: string): void {
    this.clickHandlers = this.clickHandlers.filter(h => h.id !== id);
  }


  /**
   * Emit map click event — priority handlers may consume the click (e.g. uploaded geo layers).
   */
  emitMapClick(event: MapClickEvent): void {
    for (const {handler} of this.clickHandlers) {
      if (handler(event)) {
        return;
      }
    }
    this.mapClickSubject.next(event);
  }

  /**
   * Emit vector tile feature click
   */
  emitVectorTileFeatureClick(feature: any, layerId: string, latlng: { lat: number, lng: number }): void {
    this.vectorTileFeatureClickSubject.next({ feature, layerId, latlng });
  }

  /**
   * Show popup on map
   */
  showPopup(lat: number, lng: number, content: string): void {
    if (!this.mapInstance) return;

    L.popup()
      .setLatLng([lat, lng])
      .setContent(content)
      .openOn(this.mapInstance);
  }

  /**
   * Close any open popup
   */
  closePopup(): void {
    if (this.mapInstance) {
      this.mapInstance.closePopup();
    }
  }

  /**
   * Emit map double click event
   */
  emitMapDblClick(event: MapClickEvent): void {
    this.mapDblClickSubject.next(event);
  }

  /**
   * Emit map right click event
   */
  emitMapRightClick(event: MapClickEvent): void {
    this.mapRightClickSubject.next(event);
  }

  /**
   * Emit marker click event
   */
  emitMarkerClick(event: MarkerClickEvent): void {
    this.markerClickSubject.next(event);
  }

  /**
   * Emit polygon click event
   */
  emitPolygonClick(event: MarkerClickEvent): void {
    this.polygonClickSubject.next(event);
  }

  /**
   * Emit coordinates change event
   */
  emitCoordinatesChange(coords: [number, number]): void {
    this.coordinatesChangeSubject.next(coords);
  }

  /**
   * Emit map move event
   */
  emitMapMove(event: MapMoveEvent): void {
    this.mapMoveSubject.next(event);
  }

  emitMapDragEnd(event: MapMoveEvent): void {
    this.mapDragEndSubject.next(event);
  }

  /**
   * Emit map zoom change event
   */
  emitMapZoomChange(zoom: number): void {
    this.mapZoomChangeSubject.next(zoom);
  }

  /**
   * Emit map mouse move event
   */
  emitMapMouseMove(coords: MapMouseMoveEvent): void {
    this.mapMouseMoveSubject.next(coords);
  }

}
